import { SiloArtCanvas, useSiloArt } from "@/components/SiloArtLayer";
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkle, Books, Wrench, Gear, ArrowLeft, ArrowSquareOut, Microphone, TestTube, ChartLine, ChatsCircle, Users, Funnel, Lock, Robot, CaretRight, CaretLeft, PlayCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, clearAuth, getUser } from "@/lib/api";
import Library from "@/components/Library";
import Workbench from "@/components/Workbench";
import FileRefinery from "@/components/FileRefinery";
import SiloSettings from "@/components/SiloSettings";
import ToneArchitect from "@/components/ToneArchitect";
import ProbeTab from "@/components/ProbeTab";
import OperationsTab from "@/components/OperationsTab";
import ConversationsTab from "@/components/ConversationsTab";
import YtTwinTab from "@/components/YtTwinTab";
import UsersTab from "@/components/UsersTab";
import DynamicBumps from "@/components/DynamicBumps";
// Feb 2026 (Randy pt-ytre-p1) — YTRE (YouTube Response Engine): co-watch a video
// with your silo, capture transcript slices, run Workbench ops on them, invite
// the silo to respond with a nudge stance. Owner-only tab (not in COLLAB_TABS)
// — YTRE uses the OWNER's YouTube OAuth + silo, so a collab wouldn't have a
// meaningful workflow on someone else's silo.
import YtreTab from "@/components/YtreTab";
// Feb 22, 2026 — Staging tab (in-silo capture pad + scheduled pings).
import StagingTab from "@/components/StagingTab";
import CalendarApprovalBanner from "@/components/CalendarApprovalBanner";
import IdentityPill from "@/components/IdentityPill";
import { SiloKindConvertModal } from "@/components/SiloKindPill";
import SiloImageStudio from "@/components/SiloImageStudio";
import SiloAnywhereSelfHost from "@/components/SiloAnywhereSelfHost";
import useSiloManifest from "@/lib/useSiloManifest";
import { Lightning } from "@phosphor-icons/react";
import { useEntitlements } from "@/lib/entitlements";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";

// Jul 2026 (Randy) — tabs whose ENTRY is a paid Creator Pro feature. A locked
// tab shimmers (frameless) and tapping it pops the upgrade CTA instead of
// switching. Refinery gate moved to the tab level (was Run-button only).
// Feb 2026 (Randy pt-ytre-storefront) — YTRE tab added; gated on the `ytre` SKU.
const TAB_SKU = {
  "file-refinery": "file_refinery",
  "staging": "staging",
  "ytre": "ytre",
  // Oct 2026 (Randy) — the YT Digital Twin tab is locked only when BOTH YouTube
  // SKUs are locked; owning either one has always been enough to use the
  // replies console, and the console still gates per channel inside itself.
  "yt-twin": ["youtube_twin", "yt_ai_replies"],
};

// Oct 2026 (Randy) — `conversations` was the Chats tab, which hosted the YT
// Replies console at its bottom. The console now owns the `yt-twin` tab and the
// conversation list owns `chats`. This alias must live FOREVER: a home-screen
// icon installed before the split has `?tab=conversations` baked into its
// manifest (start_url is frozen at install time), and that icon was installed to
// reach the YouTube work.
const TAB_ALIASES = { conversations: "yt-twin" };
const resolveTabId = (id) => TAB_ALIASES[id] || id;

const TABS = [
  { id: "library", label: "Library", icon: Books },
  // Staging slots in between Library and Workbench, per Randy's spec.
  { id: "staging", label: "Staging", icon: Lightning },
  { id: "workbench", label: "Workbench", icon: Wrench },
  // Oct 2026 (Randy) — "the feature is so huge, and it's sort of hidden in
  // workbench and the chats tab. it needs its own." Sits between Workbench and
  // Refinery; the Chats tab keeps its old slot below.
  { id: "yt-twin", label: "YT Digital Twin", icon: Robot },
  { id: "file-refinery", label: "Refinery", icon: Funnel },
  { id: "chats", label: "Chats", icon: ChatsCircle },
  // Feb 2026 (Randy pt-ytre-tab) — YTRE (YouTube Response Engine) sits between
  // Chats and Users. Label stays "YTRE" all-caps (short, memorable); the
  // in-tab header expands to "YouTube Response Engine" for clarity.
  { id: "ytre", label: "YTRE", icon: PlayCircle },
  { id: "users", label: "Users", icon: Users },
  { id: "operations", label: "Ops", icon: ChartLine },
  { id: "probe", label: "Probe", icon: TestTube },
  { id: "tone", label: "Tone", icon: Microphone },
  { id: "settings", label: "Settings", icon: Gear },
];

// Jun 2026 (Randy) — tabs a COLLABORATOR (non-owner) may use. The owner-only
// surfaces (Chats = the owner's audience conversations, Users = the owner's
// audience, Ops = the owner's billing/earnings) are hidden so a collaborator
// no longer triggers the "Silo not found / you don't own it" toasts.
const COLLAB_TABS = ["library", "staging", "workbench", "file-refinery", "tone", "probe", "settings"];
// (Neither `yt-twin` nor `chats` is collaborator-visible — both are owner-only.)

export default function SiloManage() {
  const { siloId } = useParams();
  const nav = useNavigate();
  const { locked: cpLocked, lockStyles: cpLockStyles } = useEntitlements();
  const { openGate: cpOpenGate } = useCreatorProGate();
  const [silo, setSilo] = useState(null);
  // Oct 2026 (Randy pt-launch-target) — tabs are addressable via ?tab=<id>, so
  // a home-screen icon (or a shared link) can open straight into Chats or
  // YTRE. sessionStorage stays the fallback when no param is present, so
  // ordinary in-app navigation behaves exactly as it did before.
  const readTabParam = () => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      const resolved = resolveTabId(t);
      return resolved && TABS.some((x) => x.id === resolved) ? resolved : null;
    } catch { return null; }
  };
  const readTabMemory = () => {
    try {
      const memo = resolveTabId(sessionStorage.getItem(`silo_tab:${siloId}`));
      return memo && TABS.some((x) => x.id === memo) ? memo : null;
    } catch { return null; }
  };
  const [tab, setTabState] = useState(() => readTabParam() || readTabMemory() || "library");
  // Per-silo tab memory (same session) so returning to a silo lands on your last
  // tab. sessionStorage auto-clears on browser close — no TTL needed, no stacking.
  const setTab = (id) => {
    setTabState(id);
    try { sessionStorage.setItem(`silo_tab:${siloId}`, id); } catch { /* */ }
    try {
      const params = new URLSearchParams(window.location.search);
      params.set("tab", id);
      window.history.replaceState(
        {}, "", `${window.location.pathname}?${params.toString()}${window.location.hash}`,
      );
    } catch { /* */ }
  };
  useEffect(() => {
    setTab(readTabParam() || readTabMemory() || "library");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siloId]);
  // Jun 2026 (Randy) — no scroll-position recall. Every tab always starts at the
  // very top so the title confirms the page. (Open/closed state of panels is
  // still remembered; only the up/down scroll position is reset.)
  useEffect(() => { try { window.scrollTo(0, 0); } catch { /* */ } }, [tab]);
  // Jun 2026 (Randy) — cross-tab handoff. Build-as-you-go lives in the YT
  // Replies console, which also renders inside the Chats tab; "Send to
  // Workbench" from there has to switch tabs before the YouTube Tool can take
  // the URL. Every tab stays mounted, so this is a state flip, not a reload.
  useEffect(() => {
    const onSwitch = (ev) => {
      const d = ev?.detail || {};
      if (!d.tab) return;
      if (d.silo_id && d.silo_id !== siloId) return;
      setTab(resolveTabId(d.tab));
    };
    window.addEventListener("mysilo:switch-silo-tab", onSwitch);
    return () => window.removeEventListener("mysilo:switch-silo-tab", onSwitch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siloId]);
  // Silo Art V2 — each internal tab dresses the content canvas with its own
  // correlated cube face (Library → home/main, everything else → same-named face).
  const { setActiveFace: setArtFace } = useSiloArt();
  useEffect(() => { setArtFace(tab === "library" ? "center" : tab); }, [tab, setArtFace]);
  // Feb 22, 2026 — list of all the user's silos, passed to the Staging tab
  // so the "push to a different silo" picker can populate immediately.
  const [allSilos, setAllSilos] = useState([]);
  // Feb 24, 2026 (Randy — YTRE activity alerts) — small pip that
  // renders up-and-right of the "YTRE" tab label when this silo has
  // unread YouTube reply activity. Fetched once on silo mount + when
  // the user switches into the YTRE tab (which force-scans). Uses the
  // same summary endpoint the Dashboard polls, filtered to this silo.
  const [ytreUnreadCount, setYtreUnreadCount] = useState(0);
  useEffect(() => {
    if (!siloId) return;
    let cancelled = false;
    const fetch = async () => {
      try {
        const r = await api.get(`/ytre/silo/${siloId}/activity`);
        if (!cancelled) setYtreUnreadCount(r.data?.unread_count || 0);
      } catch { /* silent */ }
    };
    fetch();
    // Also fire a background scan every time the silo mounts — cheapest
    // pass hits YouTube quota, per-URL / per-silo cooldown guards
    // collapse redundant work. Fire and forget.
    api.post(`/ytre/silo/${siloId}/activity/scan`).then(() => {
      if (!cancelled) fetch();  // refresh unread count post-scan
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [siloId]);
  // Auto-dismiss the tab pip when the user enters YTRE — the tab
  // itself is where they'd handle the alerts.
  useEffect(() => {
    if (tab === "ytre" && ytreUnreadCount > 0) {
      // We don't dismiss the backend state here — the in-tab banner
      // handles per-URL loads and dismisses. Just visually hide the
      // pip so we don't nag while they're already looking.
      // (Backend dismiss happens on Load-into-YTRE / Dismiss-all click.)
    }
  }, [tab, ytreUnreadCount]);
  // Jun 2026 (Randy) — link library alert pip on the Settings tab. *"New users
  // are not going to know that there is a growing list of URLs in their data
  // that needs a manual rename button pressed to fix things."* Silent unless
  // Relevant links is ON and something is actually pending; the drawer
  // broadcasts fresh counts after every fix so the dot clears as he works.
  const [linkHealth, setLinkHealth] = useState(null);
  useEffect(() => {
    if (!siloId) return;
    let cancelled = false;
    const read = async () => {
      try {
        const r = await api.get(`/silos/${siloId}/link-library/health`);
        if (!cancelled) setLinkHealth(r.data);
      } catch { /* collaborators and non-owners simply get no pip */ }
    };
    read();
    const onChanged = (e) => { if (e.detail) setLinkHealth(e.detail); else read(); };
    window.addEventListener("links:changed", onChanged);
    return () => { cancelled = true; window.removeEventListener("links:changed", onChanged); };
  }, [siloId]);

  // Jul 2026 (Randy) — the master AI Assistant is a HIDDEN tab tucked left of
  // "Library" behind a tiny caret. Clicking the caret slides the tabs right and
  // reveals it for 5s; if not clicked it slides back and re-hides.
  const [assistantRevealed, setAssistantRevealed] = useState(false);
  // Feb 2026 (Randy) — silo type conversion modal.
  const [kindConvert, setKindConvert] = useState(false);
  const revealTimerRef = useRef(null);
  const revealAssistant = () => {
    setAssistantRevealed(true);
    if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    revealTimerRef.current = setTimeout(() => setAssistantRevealed(false), 5000);
  };
  const hideAssistant = () => {
    if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    revealTimerRef.current = null;
    setAssistantRevealed(false);
  };
  useEffect(() => () => { if (revealTimerRef.current) clearTimeout(revealTimerRef.current); }, []);

  // Feb 2026 (Randy pt-siloart-quickaccess-nav-fix) — when the identity pill
  // opens Silo Art V2 while the user is inside a silo (e.g. on YTRE), fire
  // the `mysilo:open-studio` event and mount the studio HERE so we don't
  // have to bounce through /dashboard. Studio-triggered onClose leaves the
  // user exactly where they were (same silo, same tab, same scroll pos).
  const [studioSilo, setStudioSilo] = useState(null);
  useEffect(() => {
    const openFromSession = () => {
      let raw = null;
      try { raw = sessionStorage.getItem("mysilo_open_studio"); } catch { return; }
      if (!raw) return;
      let payload;
      try { payload = JSON.parse(raw); } catch { return; }
      if (!payload || !payload.siloId) return;
      // Only open here if the studio is targeting THIS silo. Otherwise let
      // Dashboard handle it (identity pill will nav there for cross-silo).
      if (payload.siloId !== siloId) return;
      try { sessionStorage.removeItem("mysilo_open_studio"); } catch { /* */ }
      setStudioSilo({ ...(silo || {}), id: payload.siloId, __stage: payload.stage });
    };
    window.addEventListener("mysilo:open-studio", openFromSession);
    return () => window.removeEventListener("mysilo:open-studio", openFromSession);
  }, [siloId, silo]);

  const load = async () => {
    try {
      const r = await api.get(`/silos/${siloId}`);
      setSilo(r.data);
    } catch (e) {
      if (e.response?.status === 401) { clearAuth(); nav("/login"); return; }
      toast.error("Could not load silo");
      nav("/dashboard");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siloId]);
  // May 24, 2026 — wire the per-silo apple-touch-icon + title hook so
  // "Add to Home Screen" from ANY creator tab (Library, Workbench,
  // Refinery, Staging, etc.) shows the silo's icon + name. The manifest
  // <link> itself is managed by the inline script in index.html — this
  // hook only handles the icon/title metadata.
  useSiloManifest(silo);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/silos");
        setAllSilos(r.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  // Jun 2026 (Randy) — if a collaborator lands on an owner-only tab (e.g. a
  // remembered "users"/"ops"), bounce them back to Library so they never hit
  // an owner-gated fetch (those panels aren't rendered for collaborators).
  useEffect(() => {
    const me = getUser();
    if (silo && me && silo.owner_user_id !== me.id && !COLLAB_TABS.includes(tab)) {
      setTab("library");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silo]);

  if (!silo) return <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-[#6B6A68]">Loading silo…</div>;

  const me = getUser();
  const isOwner = !!(silo && me && silo.owner_user_id === me.id);
  const visibleTabs = isOwner ? TABS : TABS.filter((t) => COLLAB_TABS.includes(t.id));
  // Gate the hidden AI Assistant tab on the master_ai SKU — non-owners of the
  // SKU see it locked (shimmer/badge) and clicking it opens the buffet.
  const assistantLocked = cpLocked("master_ai");
  const asstLockStyleArr = assistantLocked ? (cpLockStyles || []) : [];
  const asstLockClasses = assistantLocked ? `cp-lock ${asstLockStyleArr.map((s) => `cp-lock--${s}`).join(" ")}` : "";
  const asstShowBadge = asstLockStyleArr.includes("lock_badge");

  return (
    <div className="min-h-screen bg-[#FDFCF8] relative isolate" data-testid="silo-manage-page"
      style={{
        // iter-35g Phase 2 polish: inset main content when a dynamic bump
        // drawer is pinned. Resets to 0 when nothing is pinned.
        paddingLeft: "var(--mysilo-drawer-left, 0px)",
        paddingRight: "var(--mysilo-drawer-right, 0px)",
        transition: "padding 200ms ease",
      }}
    >
      <SiloArtCanvas />
      <header className="border-b hairline bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/dashboard" data-testid="back-to-dashboard" className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]">
              <ArrowLeft size={18} />
            </Link>
            {/* iter-35e: site-wide rule — clicking the logo always goes to
                the landing page. Auth is preserved so the user is still
                logged in when they get there (Landing shows a "Back to my
                profile" button when logged in). */}
            <Link to="/" data-testid="logo-home" className="w-9 h-9 rounded-full bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center shrink-0 hover:opacity-80 transition-opacity"><Sparkle size={18} weight="duotone" /></Link>
            <div className="min-w-0">
              <div className="font-serif-display text-xl leading-none truncate flex items-center gap-2">
                <span className="truncate">{silo.name}</span>
                {/* Feb 2026 pt89f (Randy) — SiloKindPill removed from
                    the header (never asked for it there). If the user
                    wants to change silo type, that lives inside the
                    Silo Settings tab now via the Convert modal. */}
              </div>
              {silo.tagline && (
                <div className="text-xs text-[#6B6A68] italic mt-1 truncate" data-testid="silo-tagline">{silo.tagline}</div>
              )}
            </div>
          </div>
          {/* Jun 18, 2026 — same-tab nav (removed target="_blank"). With
              per-tab sessions, a new tab starts signed-out, so this opened the
              public page "half logged-out" (no identity pill). Same-tab keeps
              the owner's session so they see their own public view correctly. */}
          {/* Jul 2026 (Randy) — Public button shifted left to make room for the
              identity pill/star, which was missing on the silo tabs. Silo
              Library lives in that pill (below Creator Dashboard). */}
          <div className="flex items-center gap-2 shrink-0">
            <a href={`/s/${silo.slug}`} data-testid="open-public-silo"
              className="text-sm px-3 py-2 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5 shrink-0">
              <ArrowSquareOut size={14} /> <span>Public QA</span>
            </a>
            <IdentityPill siloId={silo.id} siloSlug={silo.slug} />
          </div>
        </div>
        {/* Tab strip — horizontally scrollable on mobile so all 6 tabs remain reachable
             on narrow viewports without crowding the silo title above. */}
        <div className="max-w-6xl mx-auto px-2 sm:px-6 border-t hairline">
          <div
            className="flex gap-1 overflow-x-auto whitespace-nowrap snap-x snap-mandatory scrollbar-hide -mb-px"
            data-testid="silomanage-tabs"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {/* Hidden AI Assistant tab: a tiny caret sits just left of "Library".
                Tapping it slides the tabs right + reveals the AI Assistant tab
                for 5s (owner-only). Feb 16 2026 (Randy) — `sticky left-0` with
                a small left inset so the caret stays visible on mobile even
                when snap-x snaps the strip to the active tab. Chevron
                directions swapped so the hidden-state arrow points LEFT
                (Randy's "flipped vertically" preference). */}
            {isOwner && (
              <button
                type="button"
                onClick={() => (assistantRevealed ? hideAssistant() : revealAssistant())}
                data-testid="assistant-reveal-caret"
                aria-label={assistantRevealed ? "Hide AI Assistant tab" : "Reveal AI Assistant tab"}
                title={assistantRevealed ? "Hide AI Assistant" : "AI Assistant"}
                className="sticky -left-1.5 z-10 shrink-0 self-center pl-0.5 pr-0.5 py-2.5 text-[#6B6A68] hover:text-[#4A5D4E] transition-colors bg-transparent"
              >
                {assistantRevealed ? <CaretRight size={16} weight="bold" /> : <CaretLeft size={16} weight="bold" />}
              </button>
            )}
            <AnimatePresence initial={false}>
              {isOwner && assistantRevealed && (
                <motion.button
                  key="tab-assistant"
                  type="button"
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: "auto", opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.32, ease: "easeOut" }}
                  onClick={() => { hideAssistant(); if (assistantLocked) { cpOpenGate("master_ai"); } else { nav(`/silo/${silo.slug}/assistant`); } }}
                  data-testid="tab-assistant"
                  className={`relative px-2.5 sm:px-3 py-2.5 text-[13px] flex items-center gap-1 border-b-2 border-transparent text-[#4A5D4E] hover:text-[#1C1B1A] transition-colors snap-start shrink-0 overflow-hidden whitespace-nowrap font-medium ${asstLockClasses}`}
                >
                  {asstShowBadge && <Lock size={11} weight="fill" className="text-[#C16E5A]" aria-hidden="true" />}
                  <Robot size={15} weight="duotone" /> AI Assistant
                </motion.button>
              )}
            </AnimatePresence>
            {visibleTabs.map((t) => {
              const sku = TAB_SKU[t.id];
              const skuList = Array.isArray(sku) ? sku : (sku ? [sku] : []);
              // Jul 2026 (Randy 1A) — household inheritance: on personal silos a
              // collaborator rides the owner's unlock, so the tab isn't locked.
              // Oct 2026 — a tab listing several SKUs is locked only when EVERY
              // one of them is locked (owning any single one opens the tab).
              const tabLocked = skuList.length > 0 && skuList.every(
                (k) => cpLocked(k) && !(silo.inherited_skus || []).includes(k),
              );
              // Jul 2026 (Randy) — locked tabs now wear the SAME admin-selected
              // lock look(s) as gated buttons (they stack), not a hardcoded shimmer.
              const styles = tabLocked ? (cpLockStyles || []) : [];
              const lockClasses = tabLocked
                ? `cp-lock ${styles.map((s) => `cp-lock--${s}`).join(" ")}`
                : "";
              const showBadge = styles.includes("lock_badge");
              return (
                <button key={t.id}
                  onClick={() => (tabLocked ? cpOpenGate(sku) : setTab(t.id))}
                  data-testid={`tab-${t.id}`}
                  className={`relative px-2.5 sm:px-3 py-2.5 text-[13px] flex items-center gap-1 border-b-2 transition-colors snap-start shrink-0 ${tab === t.id ? "border-[#4A5D4E] text-[#1C1B1A]" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"} ${lockClasses}`}>
                  {showBadge && <Lock size={11} weight="fill" className="text-[#C16E5A]" aria-hidden="true" />}
                  <t.icon size={15} weight="duotone" /> {t.label}
                  {/* Feb 24, 2026 (Randy — YTRE activity alerts) —
                      super-tiny coral pip up-and-right of the "YTRE"
                      label. Only renders when this silo has unread
                      YouTube reply activity AND user isn't currently
                      looking at the YTRE tab (no nagging when they're
                      already there). */}
                  {t.id === "ytre" && ytreUnreadCount > 0 && tab !== "ytre" && (
                    <span
                      data-testid={`tab-ytre-alert-pip`}
                      title={`${ytreUnreadCount} recent video${ytreUnreadCount === 1 ? "" : "s"} with new replies`}
                      className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#E86A5C] ring-1 ring-white/80 shadow-sm"
                      aria-label={`${ytreUnreadCount} recent video${ytreUnreadCount === 1 ? "" : "s"} with new replies`}
                    />
                  )}
                  {/* Jun 2026 (Randy) — same pip, for links in the library that
                      still need a name (and duplicates once there are five or
                      more). Only while Relevant links is actually on.
                      LINKS_CONSOLE_SPRINT.md · §6 — it used to carry
                      `&& tab !== "settings"`, which hid it on the exact page he
                      went looking for it on. The dot's job is to keep pointing
                      at the work until the count reaches zero, so it no longer
                      stands down just because he arrived. */}
                  {t.id === "settings" && (linkHealth?.pending || 0) > 0 && (
                    <span
                      data-testid="tab-settings-links-pip"
                      title={[
                        linkHealth.needs_name
                          ? `${linkHealth.needs_name} link${linkHealth.needs_name === 1 ? "" : "s"} still need${linkHealth.needs_name === 1 ? "s" : ""} a name`
                          : "",
                        linkHealth.duplicates >= 5 ? `${linkHealth.duplicates} duplicates` : "",
                      ].filter(Boolean).join(" · ")}
                      className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#E86A5C] ring-1 ring-white/80 shadow-sm"
                      aria-label="Your link library needs attention"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="siloart-copy max-w-6xl mx-auto px-6 py-10">
        {(silo.silo_kind === "personal" || silo.silo_kind === "organizer") && <CalendarApprovalBanner silo={silo} onOpen={() => setTab("staging")} />}
        {/* All tabs stay mounted — switching never wipes slot/staging/result/probe state. */}
        <div className={tab === "library" ? "" : "hidden"}><Library siloId={silo.id} siloName={silo.name} onSwitchTab={setTab} active={tab === "library"} /></div>
        <div className={tab === "staging" ? "" : "hidden"}><StagingTab siloId={silo.id} siloName={silo.name} siloSlug={silo.slug} allSilos={allSilos} inheritedSkus={silo.inherited_skus || []} /></div>
        <div className={tab === "tone" ? "" : "hidden"}><ToneArchitect siloId={silo.id} siloName={silo.name} onSwitchTab={setTab} /></div>
        <div className={tab === "workbench" ? "" : "hidden"}><Workbench siloId={silo.id} siloName={silo.name} siloSlug={silo.slug} /></div>
        <div className={tab === "file-refinery" ? "" : "hidden"}>
          {/* iter-35c: File Refinery — duplicate of Workbench surface, but the
              Run button kicks off a refinery job over the slot contents
              (same engine as Distill Silo). Independent slot/staging state
              from Workbench via `surface=file_refinery`. */}
          <FileRefinery siloId={silo.id} siloName={silo.name} siloSlug={silo.slug} />
        </div>
        <div className={tab === "yt-twin" ? "" : "hidden"}>{isOwner && <YtTwinTab siloId={silo.id} siloSlug={silo.slug} active={tab === "yt-twin"} />}</div>
        <div className={tab === "chats" ? "" : "hidden"}>{isOwner && <ConversationsTab siloId={silo.id} siloSlug={silo.slug} />}</div>
        <div className={tab === "ytre" ? "" : "hidden"}>{isOwner && <YtreTab siloId={silo.id} siloName={silo.name} siloSlug={silo.slug} active={tab === "ytre"} allSilos={allSilos} />}</div>
        <div className={tab === "users" ? "" : "hidden"}>{isOwner && <UsersTab scope="silo" siloId={silo.id} />}</div>
        <div className={tab === "probe" ? "" : "hidden"}><ProbeTab siloId={silo.id} siloName={silo.name} /></div>
        <div className={tab === "operations" ? "" : "hidden"}>{isOwner && <OperationsTab siloId={silo.id} siloName={silo.name} />}</div>
        <div className={tab === "settings" ? "" : "hidden"}><SiloSettings silo={silo} onUpdated={(s) => setSilo(s)} /></div>
      </main>
      {/* iter-35g Phase 2 — dynamic bump drawers (Workbench/Refinery slots).
          Renders only when the current user owns the silo. */}
      <DynamicBumps silo={silo} />
      {/* Oct 2026 (Randy, P7) — Silo Anywhere on this silo's own pages, for the
          owner and collaborators only, so they can test and tune the assistant
          the way a website visitor will meet it. Draws nothing unless the owner
          switched it on for these pages. */}
      <SiloAnywhereSelfHost siloId={silo.id} />
      {kindConvert && (
        <SiloKindConvertModal
          siloId={silo.id}
          currentKind={silo.silo_kind || "public"}
          currentYtTwin={!!silo.is_youtube_twin}
          onClose={() => setKindConvert(false)}
          onSaved={(updated) => setSilo(updated)}
        />
      )}
      {/* Feb 2026 (Randy pt-siloart-quickaccess-nav-fix) — in-silo mount of the
          Silo Art V2 studio. When the identity pill's quick-access opens Silo
          Art here, we DON'T route the user through /dashboard — we open the
          studio right here so closing it lands them exactly where they were. */}
      {studioSilo && (
        <SiloImageStudio
          silo={studioSilo}
          open={!!studioSilo}
          isFounder={me?.role === "super_admin"}
          onClose={() => setStudioSilo(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
